import { Accordion } from '@common/components/Accordion';
import { InfoText } from '@common/components/InfoText';
import { Box, Divider, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useCallback, useEffect, useMemo,  useState } from 'react';

import { GroupPanelCard } from './components/GroupPanelCard';
import { Header } from './components/Header';
import { Simulation, SimulationGroup } from '@thive/fddm-types';
import { SimulationServiceFactory } from '@/modules/simulation/service/SimulationServiceFactory';
import { SimulationUtil } from '@/modules/simulation/SimulationUtil';
import { UIBlockingLoadIndicator } from '@/modules/simulation/components/UIBlockingLoadIndicator';

type Props = {
  group: SimulationGroup;
  onEditSimulationGroup: (simulationGroup: SimulationGroup, name: string) => void;
  onClose: () => void;
};

const containerSx = {
  overflowY: 'scroll',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
};

export const GroupPanel = ({ group, onEditSimulationGroup, onClose }: Props) => {
  const { t } = useTranslation('interface');
  const simSvc = useMemo(
    () => SimulationServiceFactory.getInstance().createSimulationService(),
    [],
  );
  const runningSimulationsUpdateDelayInMs = 10000;

  const [loading, setLoading] = useState<boolean>(false);
  const [simulationGroup, setSimulationGroup] = useState<SimulationGroup>(group);
  const [simulations, setSimulations] = useState<Simulation[]>();
  const [hasRunningSimulations, setHasRunningSimulations] = useState<boolean>(false);
  const [nextSimulationName, setNextSimulationName] = useState<string>('');

  const load = useCallback(
    async (showLoadingIndicator: boolean) => {
      try {
        if (showLoadingIndicator) {
          setLoading(true);
        }

        setSimulationGroup(await simSvc.getSimulationGroup(simulationGroup.id));
        setSimulations(await simSvc.getSimulationsInGroup(simulationGroup.id));
      } finally {
        if (showLoadingIndicator) {
          setLoading(false);
        }
      }
    },
    [simulationGroup, simSvc],
  );

  const onEdit = useCallback(
    async (simulationGroup: SimulationGroup) => {
      onEditSimulationGroup(simulationGroup, nextSimulationName);
    },
    [onEditSimulationGroup, nextSimulationName],
  );

  const onRerun = useCallback(
    async (group: SimulationGroup) => {
      await simSvc.reRunSimulationGroup(group.id);
    },
    [simSvc],
  );

  useEffect(() => {
    if (!simulations) {
      load(true);
    }

    const interval = setInterval(() => {
      const runningSimulations =
        simulations?.filter((s) => SimulationUtil.isRunningSimulation(s)) ?? [];
      setHasRunningSimulations(runningSimulations.length > 0);
      if (hasRunningSimulations) {
        load(false);
      }
    }, runningSimulationsUpdateDelayInMs);

    return () => clearInterval(interval);
  }, [hasRunningSimulations, load, simulations]);

  if (loading) {
    return <UIBlockingLoadIndicator block={loading} />;
  }

  return (
    <Stack gap={1} data-testid='GroupPanel' divider={<Divider />} sx={{ pt: 1 }}>
      <Header
        simulationGroup={simulationGroup}
        onEdit={onEdit}
        onRerun={onRerun}
        onClose={onClose}
      />
      <Accordion title={t('simulation.simulation_list.header')} defaultExpanded sx={{ px: 2 }}>
        {/* <SelectedSimulationsInfo sx={{ margin: '15px 0 20px' }} /> */}
        {simulations?.length ? (
          <Stack sx={containerSx}>
            {hasRunningSimulations && (
              <Box sx={{ mt: 2, p: 2, backgroundColor: 'others.lighter' }}>
                <Typography fontSize='10px'>
                  {t('simulation.simulation_list.in_progress')}
                </Typography>
              </Box>
            )}
            {simulations.map((simulation) => (
              <GroupPanelCard key={simulation.id} simulation={simulation} />
            ))}
          </Stack>
        ) : (
          <InfoText
            header={t('simulation.simulation_list.no_simulations.title')}
            subHeader={t('simulation.simulation_list.no_simulations.subtitle')}
            sx={{ mt: 4, mb: 4, ml: 2, mr: 2 }}
          />
        )}
      </Accordion>
    </Stack>
  );
};
